package dev.lingvo.site.pages.app.settings.usage

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.AgentConnectionDto
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.ErrorState
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.tabularNumbers
import dev.lingvo.site.ds.formatCount
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.pages.app.settings.UsageDenied
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import dev.lingvo.shared.UpdateAgentLimitsRequest
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCoinsIcon
import dev.lingvo.site.ds.LSquarePenIcon
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.pages.app.settings.reloadWorkspaceProtectedResource
import dev.lingvo.site.pages.app.settings.rememberWorkspaceAuthorityState
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.runWorkspaceAction
import kotlinx.coroutines.launch
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf

/**
 * Who is allowed to spend, and how much.
 *
 * The only things in this workspace that can spend credits without a person clicking are agent
 * connections, so this is a view of their budgets — one page that answers "what could cost us money
 * while nobody is looking", which is the question somebody is actually asking.
 */
@Page("/app/settings/usage/budgets")
@Layout(".components.layouts.AppLayout")
@Composable
fun BudgetsPage() {
    val workspaceContext = WorkspaceRequestContext.current()
    var refreshKey by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(0) }
    var editing by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    var busy by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf(false) }
    var message by rememberWorkspaceAuthorityState(workspaceContext) { mutableStateOf<String?>(null) }
    val claim = WorkspaceActionClaim.capture(workspaceContext, WorkspacePrivilege.AGENTS)

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(
            t("app.settings.usage.budgets.budgets", "Budgets"),
            t("app.settings.usage.budgets.what_can_spend_credits_and_how", "What can spend credits, and how much."),
            mark = { LCoinsIcon() },
        )

        // `GET /agents` calls `requireManage()`. See `UsagePage` for why this is a sentence rather
        // than an error a translator can retry for ever.
        if (claim == null) {
            UsageDenied()
            return@Column
        }

        // Above every control, because it is the limit of the whole feature and not a footnote to
        // it. Somebody who sets a budget expecting it to cap their DeepL bill has misunderstood
        // what this page can do, and the only useful moment to say so is before they set one.
        HonestLimitWell()

        val reloadable = reloadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.AGENTS,
            reloadToken = refreshKey,
            transport = ApiClient::agents,
        )
        // Never on screen at load: it exists only because a save just failed.
        message?.let {
            LAlert(
                t("app.settings.usage.budgets.that_didnt_work", "That didn't work"),
                it,
                variant = LAlertVariant.WARNING,
                live = true,
            )
        }
        LAsyncContent(
            reloadable.state,
            rows = 3,
            loadingLabel = t("app.settings.usage.budgets.loading_budgets", "Loading budgets"),
            // Client-owned copy, and the second sentence is the one that matters: a page that
            // cannot read the caps is not a page where the caps stopped working.
            failure = {
                ErrorState(
                    budgetsUnreadableNote(),
                    title = t("app.settings.usage.budgets.we_cant_read_your_caps_right", "We can't read your caps right now"),
                    onRetry = { refreshKey++ },
                )
            },
            gap = Space.XL,
        ) { connections ->
            val agents = connections.orEmpty()
            BandCaption(t("app.settings.usage.budgets.agent_connections", "Agent connections"))
            if (agents.isEmpty()) {
                // The good state, and it reads as one — one sentence, no illustrated empty
                // state. Nothing is missing here; nothing can spend, which is the answer the
                // reader came for.
                LMuted(t("app.settings.usage.budgets.nothing_can_spend_on_its_own", "Nothing can spend on its own."))
            } else {
                // **Every agent, not only the ones with a budget.** Filtering to spenders made
                // this page answer "what is currently capped" while presenting itself as the
                // answer to "what could cost us money" — and an agent with a zero budget is one
                // press away from having one, which is exactly the press this page exists for.
                agents.forEach { agent ->
                    BudgetCard(
                        agent = agent,
                        claim = claim,
                        editing = editing == agent.id,
                        onEdit = { editing = if (it) agent.id else null },
                        busy = busy,
                        setBusy = { busy = it },
                        onSaved = { editing = null; message = null; refreshKey++ },
                        onFailure = { failure ->
                            message = failure.message?.takeIf(String::isNotBlank)
                                ?: tr(
                                    "app.settings.usage.budgets.couldnt_save_that_budget_retry",
                                    "Couldn't save that budget. Retry?",
                                )
                        },
                    )
                }
            }
            Link(path = "/app/settings/agents") { Text(t("app.settings.usage.budgets.manage_agent_connections", "Manage agent connections")) }
        }
    }
}

/** What a budget on this page can and cannot stop. Stated once, before any of them. */
@Composable
private fun HonestLimitWell() {
    val palette = ColorMode.current.toSitePalette()
    P(
        Modifier
            .fillMaxWidth()
            .backgroundColor(palette.surfaceSunken)
            .borderRadius(Radius.Control)
            .color(palette.textSecondary)
            .fontSize(TypeScale.BodySmall)
            .margin(0.px)
            .padding(topBottom = Space.LG, leftRight = Space.XL)
            .toAttrs(),
    ) {
        Text(
            t("app.settings.usage.budgets.a_cap_limits_credits_which_only", "A cap limits credits, which only Lingvo's key spends. It can't limit what a project " +
                "spends on its own provider key — we never see that bill."),
        )
    }
}

/** A band heading. Names what the rows below are, so the page reads as sections and not as a list. */
@Composable
private fun BandCaption(text: String) {
    H2(
        Modifier
            .displayFont()
            .color(ColorMode.current.toSitePalette().textPrimary)
            .fontSize(TypeScale.HeadingSmall)
            .fontWeight(700)
            .lineHeight(TypeScale.DisplayLineHeight)
            .margin(0.px)
            .toAttrs(),
    ) {
        Text(text)
    }
}

/**
 * One agent's ceilings, and the form that changes them without leaving the page.
 *
 * The editor was on `agents/{agentId}`, so raising a cap from the page that lists every cap meant
 * finding the agent again on a different page — and the most common legitimate reason anybody opens
 * this page is that a pipeline grew.
 */
@Composable
private fun BudgetCard(
    agent: AgentConnectionDto,
    claim: WorkspaceActionClaim,
    editing: Boolean,
    onEdit: (Boolean) -> Unit,
    busy: Boolean,
    setBusy: (Boolean) -> Unit,
    onSaved: () -> Unit,
    onFailure: (Throwable) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var perCall by remember(agent.id, editing) { mutableStateOf(agent.maxCreditsPerCall.toString()) }
    var daily by remember(agent.id, editing) { mutableStateOf(agent.creditBudgetDaily.toString()) }
    var monthly by remember(agent.id, editing) { mutableStateOf(agent.creditBudgetMonthly.toString()) }

    LCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().gap(Space.SM)) {
            // The name titles the three figures under it, so it is a heading and not bold text: a
            // reader with twelve agent cards had no way to move between them except by arrowing
            // through every budget line.
            Row(Modifier.fillMaxWidth().gap(Space.SM), verticalAlignment = Alignment.CenterVertically) {
                LSectionTitle(agent.name, Modifier.flexGrow(1))
                if (agent.creditBudgetMonthly <= 0) {
                    LBadge(
                        t("app.settings.usage.budgets.cannot_spend", "Cannot spend"),
                        variant = LBadgeVariant.NEUTRAL,
                    )
                }
            }
            if (agent.creditBudgetMonthly > 0) {
                Span(Modifier.tabularNumbers().fontSize(TypeScale.BodySmall).toAttrs()) {
                    Text(
                        trf(
                            "app.settings.usage.budgets.ceilings_summary",
                            "{perCall} per call · {perDay} per day · {perMonth} per month",
                            "perCall" to formatCount(agent.maxCreditsPerCall),
                            "perDay" to formatCount(agent.creditBudgetDaily),
                            "perMonth" to formatCount(agent.creditBudgetMonthly),
                        ),
                    )
                }
                // Over budget it asks rather than failing, which is worth knowing before somebody
                // panics about a number.
                LMuted(t("app.settings.usage.budgets.over_these_it_asks_an_admin", "Over these, it asks an admin instead of spending."))
            } else {
                // Names the state and the one press that changes it, rather than leaving a card
                // with three zeroes on it and no account of what that means.
                LMuted(
                    t(
                        "app.settings.usage.budgets.cannot_spend_give_it_a_budget",
                        "Cannot spend · Give it a budget. This is the default, and usually the right answer.",
                    ),
                )
            }

            if (!editing) {
                LButton(
                    t("app.settings.usage.budgets.edit_budget", "Edit budget"),
                    { onEdit(true) },
                    variant = LButtonVariant.SECONDARY,
                    size = LButtonSize.SM,
                    leadingIcon = { LSquarePenIcon() },
                    // Twelve cards of "Edit budget" are twelve identical announcements.
                    accessibleLabel = trf(
                        "app.settings.usage.budgets.edit_the_budget_for_agent",
                        "Edit the budget for {agent}",
                        "agent" to agent.name,
                    ),
                )
            } else {
                Row(Modifier.fillMaxWidth().gap(Space.SM)) {
                    LTextField(perCall, { perCall = it }, Modifier.flexGrow(1), label = t("app.settings.usage.budgets.max_per_call", "Max per call"))
                    LTextField(daily, { daily = it }, Modifier.flexGrow(1), label = t("app.settings.usage.budgets.per_day", "Per day"))
                    LTextField(monthly, { monthly = it }, Modifier.flexGrow(1), label = t("app.settings.usage.budgets.per_month", "Per month"))
                }
                LMuted(
                    t(
                        "app.settings.usage.budgets.per_call_is_a_hard_ceiling",
                        "Per call is a hard ceiling — a bigger call is refused and nobody can approve it through. Going over the day or the month raises a request instead.",
                    ),
                )
                Row(Modifier.gap(Space.SM)) {
                    LButton(
                        t("app.settings.usage.budgets.save", "Save"),
                        {
                            val actionClaim = claim.takeIf { it.remainsAuthorized(WorkspaceRequestContext.current()) }
                            if (actionClaim != null) {
                                scope.launch {
                                    runWorkspaceAction(
                                        claim = actionClaim,
                                        currentContext = WorkspaceRequestContext::current,
                                        setBusy = setBusy,
                                        transport = { workspaceSlug ->
                                            ApiClient.updateAgentLimits(
                                                workspaceSlug,
                                                agent.id,
                                                UpdateAgentLimitsRequest(
                                                    creditBudgetMonthly = monthly.toIntOrNull() ?: 0,
                                                    creditBudgetDaily = daily.toIntOrNull() ?: 0,
                                                    maxCreditsPerCall = perCall.toIntOrNull() ?: 0,
                                                ),
                                            )
                                        },
                                        onSuccess = { onSaved() },
                                        onFailure = onFailure,
                                    )
                                }
                            }
                        },
                        enabled = !busy,
                        loading = busy,
                        size = LButtonSize.SM,
                    )
                    LButton(
                        t("app.settings.usage.budgets.cancel", "Cancel"),
                        { onEdit(false) },
                        variant = LButtonVariant.GHOST,
                        size = LButtonSize.SM,
                        enabled = !busy,
                    )
                }
            }
        }
    }
}

/**
 * What a failed read of the caps says, on a page about money.
 *
 * `ErrorState` takes its message as the first positional argument and is not on the extractor's
 * positional list, so this sentence — and its two siblings on the ledger and the invoices — sat
 * outside the catalogue while the red heading above them was translated. The reassuring half is
 * the half a billing reader most needs to understand.
 */
internal fun budgetsUnreadableNote(): String = tr(
    "app.settings.usage.budgets.nothing_has_changed_anything_already_capped",
    "Nothing has changed. Anything already capped is still capped — the cap is enforced on the server, not here.",
)
